import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { Trace } from '@/features/traceability/type/Tool';
import { useMemo } from 'react';
import { getMetricAxisFormatter } from '../../utils/format';
import type { ChartConfig } from '../../type/dynamicChart';
import { COLORS } from '../../utils/palette';
import { getMetricFormatter, getMetricUnit } from '@/utils/format';
import {
  aggregateTracesByTool,
  aggregateTracesByCategory,
  aggregateTracesByReference,
} from '../../utils/trace';
import { getMetricValue, isCategoricalXAxis } from '../../utils/helper';
import { SmartLegend } from './SmartLegend';
import type { LegendItem } from './SmartLegend';
import { ChartEmptyState } from './ChartEmptyState';
import { useTranslation } from '@/hooks/useTranslation';
import { getSafeAggregationLevel } from '../../utils/chartSafety';
import { getMetricLabelKey } from '../../utils/chartDefaults';

type TLHistogramChartProps = {
  config: ChartConfig;
  traces: Trace[];
};

/** One bar: a histogram bin (numeric X axis) or a category total (categorical X axis). */
type HistogramBar = { count: number };

// Check if X-axis is a categorical dimension
// Helper to calculate histogram bins
function calculateBins(
  values: number[],
  binCount: number
): Array<{ min: number; max: number; count: number; label: string }> {
  if (values.length === 0) return [];

  const min = Math.min(...values);
  const max = Math.max(...values);
  const binWidth = (max - min) / binCount;

  // Create bins
  const bins = Array.from({ length: binCount }, (_, i) => ({
    min: min + i * binWidth,
    max: min + (i + 1) * binWidth,
    count: 0,
    label: '',
  }));

  // Count values in each bin
  values.forEach((value) => {
    const binIndex = Math.min(Math.floor((value - min) / binWidth), binCount - 1);
    if (binIndex >= 0 && binIndex < binCount) {
      bins[binIndex].count++;
    }
  });

  // Generate labels
  bins.forEach((bin) => {
    const midPoint = (bin.min + bin.max) / 2;
    bin.label = midPoint.toFixed(1);
  });

  return bins;
}

// Helper to get color based on bin value
function getBinColor(count: number, maxCount: number): string {
  const intensity = count / maxCount;
  const colorIndex = Math.floor(intensity * (COLORS.length - 1));
  return COLORS[colorIndex];
}

export function TLHistogramChart({ config, traces }: TLHistogramChartProps) {
  const { t } = useTranslation();

  // Check if X-axis is categorical dimension
  const isCategorical = isCategoricalXAxis(config.xAxis.metric);

  // Determine aggregation level based on config with safety check
  const aggregationLevel = getSafeAggregationLevel(config.aggregatedBy, traces, isCategorical);

  // Aggregate data based on X-axis type
  const aggregatedData = useMemo(() => {
    if (isCategorical) {
      // For categorical X-axis, aggregate by category
      return aggregateTracesByCategory(
        traces,
        config.xAxis.metric as 'machine' | 'material' | 'toolHolder' | 'lubrication'
      );
    }

    if (aggregationLevel === 'reference') {
      return aggregateTracesByReference(traces);
    }

    if (aggregationLevel === 'trace') {
      return traces;
    }
    // Aggregate by tool for histogram
    return aggregateTracesByTool(traces);
  }, [traces, aggregationLevel, isCategorical, config.xAxis.metric]);

  // For categorical X-axis, create simple bar chart data
  // For numeric, calculate histogram bins
  const chartData = useMemo<HistogramBar[]>(() => {
    if (isCategorical) {
      // Categorical mode: simple bar chart showing distribution by category
      return aggregatedData
        .map((item) => {
          if ('category' in item) {
            const yValue = getMetricValue(item, config.yAxis.metric);
            return {
              category: item.category,
              count: yValue || 0,
              traceCount: item.traceCount,
            };
          }
          // Reference aggregation as categorical
          if ('reference' in item && 'toolCount' in item) {
            const yValue = getMetricValue(item, config.yAxis.metric);
            return {
              category: item.reference,
              count: yValue || 0,
              traceCount: item.traceCount,
              toolCount: item.toolCount,
            };
          }
          return null;
        })
        .filter((d): d is NonNullable<typeof d> => d !== null);
    }

    // Numeric mode: calculate bins
    const values = aggregatedData
      .map((item) => getMetricValue(item, config.yAxis.metric))
      .filter((v): v is number => v !== null);

    const binCount = config.options.binCount || 10;
    return calculateBins(values, binCount);
  }, [aggregatedData, config.yAxis.metric, config.options.binCount, isCategorical]);

  // Extract numeric values for statistics (only for non-categorical mode)
  const numericValues = useMemo(() => {
    if (isCategorical) return [];

    return aggregatedData
      .map((item) => getMetricValue(item, config.yAxis.metric))
      .filter((v): v is number => v !== null);
  }, [aggregatedData, config.yAxis.metric, isCategorical]);

  // Calculate statistics (only for numeric mode)
  const statistics = useMemo(() => {
    if (isCategorical || numericValues.length === 0) return null;

    const mean =
      numericValues.reduce((sum: number, v: number) => sum + v, 0) / numericValues.length;
    const sortedValues = [...numericValues].sort((a, b) => a - b);
    const median = sortedValues[Math.floor(sortedValues.length / 2)];
    const min = sortedValues[0];
    const max = sortedValues[sortedValues.length - 1];

    return { mean, median, min, max, count: numericValues.length };
  }, [numericValues, isCategorical]);

  const maxCount = Math.max(...chartData.map((bar) => bar.count), 1);

  // Prepare legend items for SmartLegend (only for categorical with colorBy)
  const legendItems: LegendItem[] = useMemo(() => {
    if (!config.options.colorBy || !isCategorical) return [];

    return (chartData as Array<{ category: string; count: number }>).map((d, index) => ({
      value: d.category,
      label: d.category,
      color: COLORS[index % COLORS.length],
    }));
  }, [chartData, config.options.colorBy, isCategorical]);

  // Formatters
  const yAxisFormatter = getMetricAxisFormatter(config.yAxis.metric);
  const yFormatter = getMetricFormatter(config.yAxis.metric);
  const yUnit = getMetricUnit(config.yAxis.metric);

  if (chartData.length === 0) {
    return <ChartEmptyState variant="no-data" />;
  }

  return (
    <div className="space-y-2">
      <ResponsiveContainer width="100%" height={450}>
        <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 60, left: 52 }}>
          {config.options.showGridLines !== false && <CartesianGrid strokeDasharray="3 3" />}
          <XAxis
            dataKey={isCategorical ? 'category' : 'label'}
            angle={-45}
            textAnchor="end"
            height={52}
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            label={{
              value: isCategorical
                ? t(getMetricLabelKey(config.xAxis.metric))
                : `${t(getMetricLabelKey(config.yAxis.metric))} ${yUnit ? `(${yUnit})` : ''}`,
              position: 'insideBottom',
              offset: -40,
              fill: 'var(--muted-foreground)',
            }}
            tickFormatter={
              isCategorical
                ? (value: string) => String(value)
                : (value: string) => yAxisFormatter(parseFloat(value))
            }
          />
          <YAxis
            tickFormatter={isCategorical ? yAxisFormatter : undefined}
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            label={{
              value: isCategorical
                ? `${t(getMetricLabelKey(config.yAxis.metric))} ${yUnit ? `(${yUnit})` : ''}`
                : t('analytics.dashboard.complexCharts.defaults.frequencyLabel'),
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
              fill: 'var(--muted-foreground)',
            }}
          />
          <Tooltip
            content={({ payload }) => {
              if (!payload || payload.length === 0) return null;
              const data = payload[0].payload;

              if (isCategorical) {
                return (
                  <div className="bg-popover border border-border shadow-md rounded-lg p-3 text-xs">
                    <p className="font-medium">
                      {t(getMetricLabelKey(config.xAxis.metric))}: {data.category}
                    </p>
                    <p className="font-mono">
                      {t(getMetricLabelKey(config.yAxis.metric))}: {yFormatter(data.count)}
                    </p>
                    {typeof data.toolCount === 'number' && (
                      <p className="font-mono">
                        {t('analytics.dashboard.complexCharts.tooltip.toolCount', {
                          count: data.toolCount,
                        })}
                      </p>
                    )}
                    {data.traceCount > 1 && (
                      <p className="font-mono text-muted-foreground">
                        {t('analytics.dashboard.complexCharts.tooltip.aggregatedFrom', {
                          count: data.traceCount,
                        })}
                      </p>
                    )}
                  </div>
                );
              }

              return (
                <div className="bg-popover border border-border shadow-md rounded-lg p-3 text-xs">
                  <p className="font-medium">
                    {t('analytics.dashboard.complexCharts.tooltip.range')}: {yFormatter(data.min)} -{' '}
                    {yFormatter(data.max)}
                  </p>
                  <p className="font-mono">
                    {t('analytics.dashboard.complexCharts.tooltip.count')}: {data.count}
                  </p>
                  {statistics && (
                    <p className="font-mono text-muted-foreground">
                      {t('analytics.dashboard.complexCharts.tooltip.ofData', {
                        percent: ((data.count / statistics.count) * 100).toFixed(1),
                      })}
                    </p>
                  )}
                </div>
              );
            }}
          />
          <Bar
            dataKey="count"
            name={isCategorical ? t(getMetricLabelKey(config.yAxis.metric)) : t('analytics.dashboard.complexCharts.tooltip.frequency')}
            fill={COLORS[0]}
            label={
              config.options.showDataLabels
                ? {
                    position: 'top',
                    formatter: (value: unknown) => String(value),
                  }
                : undefined
            }
          >
            {config.options.colorBy === 'toolId' &&
              !isCategorical &&
              chartData.map((entry, index: number) => (
                <Cell key={`cell-${index}`} fill={getBinColor(entry.count, maxCount)} />
              ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* SmartLegend - Show only for categorical with colorBy */}
      {config.options.colorBy && isCategorical && legendItems.length > 0 && (
        <SmartLegend payload={legendItems} layout="horizontal" maxVisibleItems={10} />
      )}
    </div>
  );
}
